import { useEffect, useRef } from 'react';

const MOBILE_BREAKPOINT = 768;
const INIT_RETRY_DELAY_MS = 100;
const INIT_MAX_RETRIES = 20;
const POINT_COLOR = 'rgba(141, 255, 164,';

function getDistance(p1, p2) {
  return (p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2;
}

function easeInOutCirc(t) {
  return t < 0.5
    ? (1 - Math.sqrt(1 - (2 * t) ** 2)) / 2
    : (Math.sqrt(1 - (-2 * t + 2) ** 2) + 1) / 2;
}

function scheduleShift(point) {
  const duration = 1500 + Math.random() * 1500;
  point.shift = {
    startX: point.x,
    startY: point.y,
    targetX: point.originX - 20 + Math.random() * 40,
    targetY: point.originY - 20 + Math.random() * 40,
    startTime: performance.now(),
    duration,
  };
}

function buildPoints(width, height) {
  const isMobile = width < MOBILE_BREAKPOINT;
  const stepDivisor = isMobile ? 10 : 18;
  const stepX = Math.max(width / stepDivisor, isMobile ? 80 : 70);
  const stepY = Math.max(height / stepDivisor, isMobile ? 80 : 70);
  const points = [];

  for (let x = 0; x < width; x += stepX) {
    for (let y = 0; y < height; y += stepY) {
      const px = x + Math.random() * stepX;
      const py = y + Math.random() * stepY;
      points.push({ x: px, y: py, originX: px, originY: py, active: 0 });
    }
  }

  // Find 3 closest neighbors for each point
  points.forEach((point) => {
    const closest = [];
    points.forEach((candidate) => {
      if (candidate === point) return;
      const dist = getDistance(point, candidate);
      for (let k = 0; k < 3; k++) {
        if (!closest[k] || dist < closest[k].distance) {
          closest.splice(k, 0, { point: candidate, distance: dist });
          if (closest.length > 3) closest.pop();
          break;
        }
      }
    });
    point.closest = closest.filter(Boolean).map((item) => item.point);
    point.circle = { radius: 1 + Math.random(), active: 0 };
    scheduleShift(point);
  });

  return points;
}

export function useCanvasAnimation() {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  /** Visibility toggles frequently at the IO threshold; use a ref so we don't remount this effect (H3). */
  const isVisibleRef = useRef(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          isVisibleRef.current = e.isIntersecting;
        }),
      { threshold: 0.1, rootMargin: '50px' }
    );
    observer.observe(container);

    const ctx = canvas.getContext('2d');
    const target = { x: 0, y: 0 };
    let width = 0;
    let height = 0;
    let points = [];
    let animationFrameId = null;

    const updateCanvasSize = () => {
      const rect = container.getBoundingClientRect();
      const newW = rect.width || container.offsetWidth;
      const newH = rect.height || container.offsetHeight;
      if (newW > 0 && newH > 0 && (newW !== width || newH !== height)) {
        width = newW;
        height = newH;
        canvas.width = width;
        canvas.height = height;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        target.x = width / 2;
        target.y = height / 2;
        points = buildPoints(width, height);
      }
    };

    const animate = (now) => {
      if (!isVisibleRef.current || document.hidden) {
        animationFrameId = requestAnimationFrame(animate);
        return;
      }
      ctx.clearRect(0, 0, width, height);

      // Update positions
      points.forEach((point) => {
        if (!point.shift) scheduleShift(point);
        const progress = Math.min((now - point.shift.startTime) / point.shift.duration, 1);
        const eased = easeInOutCirc(progress);
        point.x = point.shift.startX + (point.shift.targetX - point.shift.startX) * eased;
        point.y = point.shift.startY + (point.shift.targetY - point.shift.startY) * eased;
        if (progress >= 1) scheduleShift(point);
      });

      // Draw
      points.forEach((point) => {
        const distance = getDistance(target, point);
        if (distance < 3000) { point.active = 0.08; point.circle.active = 0.15; }
        else if (distance < 15000) { point.active = 0.03; point.circle.active = 0.08; }
        else if (distance < 30000) { point.active = 0.008; point.circle.active = 0.03; }
        else { point.active = 0; point.circle.active = 0; }

        if (point.active && point.closest) {
          point.closest.forEach((cp) => {
            ctx.beginPath();
            ctx.moveTo(point.x, point.y);
            ctx.lineTo(cp.x, cp.y);
            ctx.strokeStyle = `${POINT_COLOR} ${point.active})`;
            ctx.stroke();
          });
        }
        if (point.circle?.active) {
          ctx.beginPath();
          ctx.arc(point.x, point.y, point.circle.radius, 0, 2 * Math.PI);
          ctx.fillStyle = `${POINT_COLOR} ${point.circle.active})`;
          ctx.fill();
        }
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    const handlePointerMove = (event) => {
      const rect = container.getBoundingClientRect();
      const x = event.clientX ?? event.touches?.[0]?.clientX;
      const y = event.clientY ?? event.touches?.[0]?.clientY;
      if (typeof x === 'number') { target.x = x - rect.left; target.y = y - rect.top; }
    };

    const handlePointerLeave = () => { target.x = width / 2; target.y = height / 2; };
    const handleOrientationChange = () => setTimeout(updateCanvasSize, 100);

    // Init with retry
    let attempts = 0;
    const init = () => {
      updateCanvasSize();
      if (width > 0 && height > 0) {
        animationFrameId = requestAnimationFrame(animate);
        container.addEventListener('pointermove', handlePointerMove);
        container.addEventListener('pointerleave', handlePointerLeave);
        window.addEventListener('resize', updateCanvasSize);
        window.addEventListener('orientationchange', handleOrientationChange);
      } else if (attempts++ < INIT_MAX_RETRIES) {
        setTimeout(init, INIT_RETRY_DELAY_MS);
      }
    };

    requestAnimationFrame(() => setTimeout(init, 100));

    return () => {
      observer.disconnect();
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      container?.removeEventListener('pointermove', handlePointerMove);
      container?.removeEventListener('pointerleave', handlePointerLeave);
      window.removeEventListener('resize', updateCanvasSize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  return { canvasRef, containerRef };
}
